import { Component, Project, SearchFilters, SortOption } from '../types';

export const filterComponents = (
  components: Component[],
  filters: SearchFilters
): Component[] => {
  return components.filter(component => {
    // Text search
    if (filters.query) {
      const searchText = filters.query.toLowerCase();
      const searchableFields = [
        component.name,
        component.name,
        component.description,
        ...component.tags
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (!searchableFields.includes(searchText)) {
        return false;
      }
    }

    // Type filter
    if (filters.type.length > 0 && !filters.type.includes(component.type)) {
      return false;
    }

    // Status filter
    if (filters.status && filters.status !== 'all' && component.status !== filters.status) {
      return false;
    }

    // Tags filter
    if (filters.tags.length > 0) {
      const hasMatchingTag = filters.tags.some(tag => 
        component.tags.includes(tag)
      );
      if (!hasMatchingTag) {
        return false;
      }
    }

    // CMS filter
    if (filters.cms.length > 0 && component.cms) {
      const hasMatchingCms = filters.cms.some(cms => 
        component.cms!.includes(cms)
      );
      if (!hasMatchingCms) {
        return false;
      }
    }

    // Themes filter
    if (filters.themes.length > 0 && component.themes) {
      const hasMatchingTheme = filters.themes.some(theme => 
        component.themes!.includes(theme)
      );
      if (!hasMatchingTheme) {
        return false;
      }
    }

    // Accessibility filter
    if (filters.accessibility.length > 0 && component.accessibility) {
      const hasMatchingAccessibility = filters.accessibility.some(acc => 
        component.accessibility![acc as keyof typeof component.accessibility]
      );
      if (!hasMatchingAccessibility) {
        return false;
      }
    }

    return true;
  });
};

export const sortComponents = (
  components: Component[],
  sortOption: SortOption
): Component[] => {
  return [...components].sort((a, b) => {
    let aValue: any = a[sortOption.field];
    let bValue: any = b[sortOption.field];

    // Handle type field specifically
    if (sortOption.field === 'type') {
      aValue = a.type || '';
      bValue = b.type || '';
    }

    // Handle status field specifically
    if (sortOption.field === 'status') {
      aValue = a.status || '';
      bValue = b.status || '';
    }

    // Handle date sorting
    if (sortOption.field === 'lastUpdated') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }

    // Handle string sorting
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortOption.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
};

export const searchProjects = (
  projects: Project[],
  query: string
): Project[] => {
  if (!query) return projects;
  
  const searchText = query.toLowerCase();
  return projects.filter(project => {
    const searchableFields = [
      project.name,
      project.description
    ].filter(Boolean).join(' ').toLowerCase();
    
    return searchableFields.includes(searchText);
  });
};

export const getUniqueValues = (
  components: Component[],
  field: keyof Component
): string[] => {
  const values = new Set<string>();
  
  components.forEach(component => {
    let value: any;
    
    // Handle type field specifically
    if (field === 'type') {
      value = component.type;
    } else if (field === 'status') {
      value = component.status;
    } else {
      value = component[field];
    }
    
    if (Array.isArray(value)) {
      value.forEach(v => {
        if (typeof v === 'string') {
          values.add(v);
        } else if (typeof v === 'object' && v !== null) {
          // Handle nested objects like { name: string, file: string }
          if ('name' in v && typeof v.name === 'string') {
            values.add(v.name);
          } else if ('theme' in v && typeof v.theme === 'string') {
            values.add(v.theme);
          }
        }
      });
    } else if (value) {
      values.add(value as string);
    }
  });
  
  return Array.from(values).sort();
};

export const getAccessibilityFeatures = (component: Component): { feature: string; value: boolean }[] => {
  if (!component.accessibility) return [];
  
  const features: { feature: string; value: boolean }[] = [];
  Object.entries(component.accessibility).forEach(([key, value]) => {
    features.push({ feature: key, value: !!value });
  });
  
  return features;
};

export const getComponentTypeColor = (type: string): string => {
  const colors = {
    '10-atoms': 'bg-blue-100 text-blue-800',
    '20-molecules': 'bg-green-100 text-green-800',
    '30-organisms': 'bg-purple-100 text-purple-800',
    '40-templates': 'bg-orange-100 text-orange-800',
    '50-pages': 'bg-red-100 text-red-800',
    // Fallback for old format
    atom: 'bg-blue-100 text-blue-800',
    molecule: 'bg-green-100 text-green-800',
    organism: 'bg-purple-100 text-purple-800',
    template: 'bg-orange-100 text-orange-800',
    page: 'bg-red-100 text-red-800'
  };
  
  return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
};

export const getStatusColor = (status: string): string => {
  const colors = {
    ready: 'badge-success',
    'in-progress': 'badge-warning',
    review: 'badge-info',
    deprecated: 'badge-error',
    draft: 'badge-gray',
    active: 'badge-success'
  };
  
  return colors[status as keyof typeof colors] || 'badge-gray';
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '—';
  
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  } catch (error) {
    return '—';
  }
};
